/**
 * 『ハイドレンジア』 (Hydrangea)
 * 作詞・作曲・編曲：とうかさ / MV映像：木葉はづく / イラスト：orie / タイポグラフィ：本忠学
 * Official MV: https://youtu.be/lqTS5eaEhbI
 *
 * ジェネラティブアート・コンセプト（1シーン完結・モノクロームの幾何学と紫陽花）:
 * - MV全編を貫く「白・黒・点描の灰色（灰色見出す体温で）」のストイックなモノクローム美学と、
 *   木葉はづく氏×本忠学氏による精緻な幾何学・植物モチーフを、シーン切り替えなしの1シーン完結ジェネラティブアートとして構築。
 * - 「🎲 再生成（Reroll）」ボタンを押すたびに、MVを象徴する4つの空間モードが切り替わる：
 *   1. Mode 0 [点描の天球と公転する多面体 (Dark Cosmos)] (`hydrangea_maxres3.jpg`):
 *      斜めダイヤ格子と無数の砂目点描（スティップル）リングの中心に白い天球が浮かび、
 *      正二十面体・正十二面体・正八面体などの3Dワイヤーフレーム多面体と紫陽花の小花が楕円軌道を公転する。
 *   2. Mode 1 [白磁の点描球体と多面体軌道 (White Sphere)] (`hydrangea_maxresdefault.jpg`):
 *      純白の空間に繊細な点描陰影を帯びた巨大球体が浮かび、立体多面体と3Dワイヤーフレーム直方体が静かに巡る。
 *   3. Mode 2 [「空白を埋める」四弁ダイヤ格子と市松の浸食 (Blank-Filling Diamond Grid)] (`hydrangea_z0_bot.png` / `hydrangea_z2_top.png`):
 *      紫陽花の4枚ガク（装飾花）を幾何学化した「4分割ダイヤタイル（◇）」とチェッカーボードの波が、
 *      カーソルや波紋に反応して次々と「空白を埋める」ように開花・反転するインタラクティブ格子。
 *   4. Mode 3 [傾いた方眼紙とモノクローム紫陽花の花房 (Graph-Paper Hydrangea Bouquet)] (`hydrangea_maxres2.jpg` / `hydrangea_maxres1.jpg`):
 *      斜めに傾いた方眼紙グリッドと縦スリット窓の上に、緻密な点描と線画で描かれた紫陽花の花房が咲き誇り、
 *      3Dワイヤーフレームの箱と灰色の花弁が舞い散る。
 */

export class HydrangeaGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.modeIndex = -1; // 初回initで決定、再生成で4つのモードを切り替え

    // 点描（スティップル）テクスチャキャッシュ
    this.stippleSphereDark = null;
    this.stippleSphereLight = null;

    // 公転する3D多面体（正二十面体・正八面体・正十二面体風ワイヤー＋点描面）
    this.polyhedra = [];
    // 「空白を埋める」4分割ダイヤタイル格子
    this.diamondTiles = [];
    // 紫陽花の花房（4枚ガクの小花クラスター）
    this.hydrangeaFlorets = [];
    // 浮遊する3Dワイヤーフレーム直方体（MVサビの透明な箱）
    this.wireframeBoxes = [];
    // 舞い散るモノクロームの花弁・点描粒子
    this.petals = [];
    // クリックで広がる同心円音響トンネル波紋＆開花バースト
    this.ripples = [];
    // カーソル移動軌跡
    this.cursorTrail = [];

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.pulseBoost = 0;
  }

  onReroll() {
    this.modeIndex = (this.modeIndex + 1) % 4;
    this.pulseBoost = 1.2;
    this.stippleSphereDark = null;
    this.stippleSphereLight = null;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.modeIndex < 0) {
      this.modeIndex = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.polyhedra = [];
    this.diamondTiles = [];
    this.hydrangeaFlorets = [];
    this.wireframeBoxes = [];
    this.petals = [];
    this.ripples = [];
    this.cursorTrail = [];

    // 1. 公転する3D多面体サテライト群の生成（hydrangea_maxresdefault.jpg / hydrangea_maxres3.jpg）
    const polyCount = Math.floor(5 + density * 5);
    for (let i = 0; i < polyCount; i++) {
      this.polyhedra.push({
        orbitRadiusX: p.random(145, 310) * scale,
        orbitRadiusY: p.random(52, 145) * scale,
        orbitTilt: p.random(-0.32, 0.32),
        angle: (p.TWO_PI / polyCount) * i + p.random(-0.3, 0.3),
        orbitSpeed: p.random(0.18, 0.48) * (i % 2 === 0 ? 1 : -1),
        size: p.random(18, 38) * scale,
        rotX: p.random(p.TWO_PI),
        rotY: p.random(p.TWO_PI),
        rotSpeedX: p.random(-0.025, 0.025),
        rotSpeedY: p.random(-0.025, 0.025),
        shapeType: i % 3 // 0: 正二十面体風, 1: 正八面体ダイヤ, 2: 四面体ピラミッド
      });
    }

    // 2. 「空白を埋める」4分割ダイヤタイル格子（hydrangea_z0_bot.png / hydrangea_z2_top.png）
    const tileStep = 66 * scale;
    const cols = Math.ceil(w / tileStep) + 2;
    const rows = Math.ceil(h / tileStep) + 2;
    for (let r = -1; r < rows; r++) {
      for (let c = -1; c < cols; c++) {
        const tx = c * tileStep + (r % 2 === 0 ? 0 : tileStep * 0.5);
        const ty = r * tileStep * 0.72;
        const distCenter = p.dist(tx, ty, w * 0.5, h * 0.5) / (Math.max(w, h) * 0.6);
        this.diamondTiles.push({
          x: tx,
          y: ty,
          size: tileStep * 0.62,
          fillLevel: p.random() < 0.45 ? p.random(0.3, 1.0) : 0.0,
          targetFill: p.random() < 0.45 ? 1.0 : 0.0,
          phase: distCenter * 4.0 + p.random(1.5),
          petalMask: [true, true, true, true]
        });
      }
    }

    // 3. 紫陽花の花房（4枚ガクの小花が密集した球状ブーケ — hydrangea_maxres2.jpg）
    const floretCount = Math.floor(42 + density * 36);
    const bouquetRadius = 148 * scale;
    for (let i = 0; i < floretCount; i++) {
      // フィボナッチスパイラル分布で球状に美しく密集させる
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const t = i / floretCount;
      const r = Math.sqrt(t) * bouquetRadius * p.random(0.88, 1.08);
      const theta = i * goldenAngle;
      const fx = Math.cos(theta) * r;
      const fy = Math.sin(theta) * r;
      // 球面上のZ高さ（陰影計算用）
      const nz = Math.sqrt(Math.max(0.05, 1 - (r / (bouquetRadius * 1.1)) ** 2));

      this.hydrangeaFlorets.push({
        ox: fx,
        oy: fy,
        nz,
        size: p.random(22, 38) * scale * (0.75 + 0.25 * nz),
        rot: p.random(p.TWO_PI),
        shade: p.constrain(nz * 215 + p.random(-28, 28), 55, 250),
        phase: p.random(p.TWO_PI)
      });
    }

    // 4. 浮遊する3Dワイヤーフレーム直方体（hydrangea_z1_top.png Row 1 / hydrangea_z2_top.png Row 3）
    const boxCount = Math.floor(6 + density * 6);
    for (let i = 0; i < boxCount; i++) {
      this.wireframeBoxes.push({
        xRatio: p.random(0.10, 0.90),
        yRatio: p.random(0.14, 0.86),
        w: p.random(55, 135) * scale,
        h: p.random(32, 78) * scale,
        d: p.random(24, 52) * scale,
        tilt: p.random(-0.25, 0.25),
        driftPhase: p.random(p.TWO_PI),
        depth: p.random(0.5, 1.3)
      });
    }

    // 5. 舞い散るモノクロームの花弁・点描粒子
    const petalCount = Math.floor(45 + density * 55);
    for (let i = 0; i < petalCount; i++) {
      this.petals.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-0.4, 0.4),
        vy: p.random(-0.55, -0.12),
        size: p.random(3.5, 10) * scale,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.03, 0.03),
        gray: p.random(40, 240),
        isPetal: i % 3 !== 0,
        phase: p.random(p.TWO_PI)
      });
    }

    // 6. 点描球体バッファの生成
    this.generateStippleBuffers(scale);
  }

  /**
   * MV（hydrangea_maxresdefault.jpg / hydrangea_maxres3.jpg）の象徴的な「砂目・点描（スティップル）陰影球体」を事前生成
   */
  generateStippleBuffers(scale) {
    const p = this.p;
    const sz = 320;

    // 1. 白背景用の点描球体（上半分が白く、下半分に向かって黒い点描が濃くなる — hydrangea_maxresdefault.jpg）
    this.stippleSphereLight = p.createGraphics(sz, sz);
    const gl = this.stippleSphereLight;
    gl.pixelDensity(1);
    gl.loadPixels();

    // 2. 黒背景用の点描ハロー＆白いコア球体（hydrangea_maxres3.jpg）
    this.stippleSphereDark = p.createGraphics(sz, sz);
    const gd = this.stippleSphereDark;
    gd.pixelDensity(1);
    gd.loadPixels();

    const cx = sz * 0.5;
    const cy = sz * 0.5;
    const maxR = sz * 0.46;

    for (let y = 0; y < sz; y++) {
      for (let x = 0; x < sz; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const idx = (y * sz + x) * 4;

        // --- Light Sphere (hydrangea_maxresdefault.jpg) ---
        if (dist <= maxR) {
          const nx = dx / maxR;
          const ny = dy / maxR;
          const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
          // 上からの光：下部にいくほど暗くなる三日月状の陰影
          const light = p.constrain(nz * 0.55 - ny * 0.62 + 0.38, 0, 1);
          const shadowProb = 1.0 - light;

          // 底部は濃いチャコール、中間は砂目点描ドット
          let val = 252;
          if (ny > 0.58 && nz < 0.55) {
            val = 52;
          } else if (Math.random() < Math.pow(shadowProb, 1.45) * 0.85) {
            val = Math.random() < 0.5 ? 35 : 120;
          }
          gl.pixels[idx] = val;
          gl.pixels[idx + 1] = val;
          gl.pixels[idx + 2] = val;
          gl.pixels[idx + 3] = 255;
        } else {
          gl.pixels[idx + 3] = 0;
        }

        // --- Dark Halo & Core Sphere (hydrangea_maxres3.jpg) ---
        const coreR = sz * 0.20;
        if (dist <= coreR) {
          // 中央の純白コア球体
          gd.pixels[idx] = 248;
          gd.pixels[idx + 1] = 248;
          gd.pixels[idx + 2] = 250;
          gd.pixels[idx + 3] = 255;
        } else if (dist <= maxR) {
          // 外周に広がる同心円状の砂目点描（スティップル）グレイン層
          const normD = (dist - coreR) / (maxR - coreR);
          const ringWave = 0.85 + 0.15 * Math.cos(normD * Math.PI * 6.0);
          const densityProb = (1.0 - normD * 0.82) * ringWave * 0.62;
          if (Math.random() < densityProb) {
            const v = Math.floor(150 + Math.random() * 105);
            gd.pixels[idx] = v;
            gd.pixels[idx + 1] = v;
            gd.pixels[idx + 2] = v;
            gd.pixels[idx + 3] = Math.floor((1.0 - normD * 0.7) * 235);
          } else {
            gd.pixels[idx + 3] = 0;
          }
        } else {
          gd.pixels[idx + 3] = 0;
        }
      }
    }
    gl.updatePixels();
    gd.updatePixels();
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;

    this.pulseBoost = 1.5;

    // 1. クリック地点から同心円の音響トンネル＆紫陽花波紋が広がる
    this.ripples.push({
      x,
      y,
      radius: 8,
      maxRadius: Math.max(p.width, p.height) * 0.55,
      alpha: 255,
      polySize: p.random(20, 36) * scale,
      rot: p.random(p.TWO_PI)
    });
    if (this.ripples.length > 6) {
      this.ripples.shift();
    }

    // 2. Mode 2（四弁ダイヤ格子）ではクリック位置周辺の「空白」が一斉に埋まる
    for (let i = 0; i < this.diamondTiles.length; i++) {
      const tile = this.diamondTiles[i];
      const d = p.dist(x, y, tile.x, tile.y);
      if (d < 220 * scale) {
        tile.targetFill = tile.targetFill > 0.5 ? 0.0 : 1.0;
      }
    }

    // 3. クリック地点からモノクロームの紫陽花ガク（4枚花弁）が舞い散る
    for (let i = 0; i < 14; i++) {
      const ang = p.random(p.TWO_PI);
      const spd = p.random(1.0, 3.8);
      this.petals.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        size: p.random(5, 12) * scale,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.06, 0.06),
        gray: p.random(60, 245),
        isPetal: true,
        phase: p.random(p.TWO_PI),
        temporary: true,
        alpha: 255
      });
    }
  }

  onPointerMove(x, y) {
    const p = this.p;
    this.pointerX = x;
    this.pointerY = y;

    // カーソルが通過した4分割ダイヤタイルの「空白を埋める」インタラクション
    const scale = Math.min(p.width, p.height) / 720;
    for (let i = 0; i < this.diamondTiles.length; i++) {
      const tile = this.diamondTiles[i];
      if (p.dist(x, y, tile.x, tile.y) < 68 * scale) {
        tile.targetFill = 1.0;
      }
    }

    if (p.frameCount % 2 === 0) {
      this.cursorTrail.push({
        x,
        y,
        size: p.random(8, 16) * scale,
        rot: p.random(p.TWO_PI),
        alpha: 195
      });
      if (this.cursorTrail.length > 22) {
        this.cursorTrail.shift();
      }
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.45 + motion * 1.15;
    this.time += 0.015 * dt * speedMult;
    this.pulseBoost = p.lerp(this.pulseBoost, 0, 0.06 * dt);

    // パララックス補間
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 26;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 26;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.07);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.07);

    const cx = w * 0.5 - this.parallaxX * 0.45;
    const cy = h * 0.5 - this.parallaxY * 0.45;

    const isDarkMode = this.modeIndex === 0 || this.modeIndex === 2;

    // =========================================================================
    // 1. 背景と斜め方眼紙／ダイヤ格子・チェッカーボード浸食波の描画
    // =========================================================================
    this.drawBackgroundAndGrid(w, h, scale, isDarkMode, glow);

    // =========================================================================
    // 2. モード別のメイン・ジェネラティブ構造描画（1シーン完結・再生成で切り替え）
    // =========================================================================
    if (this.modeIndex === 0) {
      // Mode 0: 漆黒の点描天球と同心円軌道・公転する3D多面体（hydrangea_maxres3.jpg）
      this.drawDarkCosmosSphereAndPolyhedra(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 1) {
      // Mode 1: 純白の点描球体と多面体軌道・カリグラフィ刻印（hydrangea_maxresdefault.jpg）
      this.drawLightStippledSphereAndPolyhedra(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 2) {
      // Mode 2: 「空白を埋める」四弁ダイヤ格子と中心の点描天球（hydrangea_z0_bot.png / hydrangea_z2_top.png）
      this.drawBlankFillingDiamondGrid(cx, cy, w, h, scale, dt, speedMult, glow);
    } else {
      // Mode 3: 傾いた方眼紙とモノクローム紫陽花の花房・縦スリット窓（hydrangea_maxres2.jpg / hydrangea_maxres1.jpg）
      this.drawGraphPaperHydrangeaBouquet(cx, cy, w, h, scale, dt, speedMult, glow);
    }

    // =========================================================================
    // 3. 浮遊する3Dワイヤーフレーム直方体（MVサビの透明な箱）
    // =========================================================================
    this.drawFloatingWireframeBoxes(w, h, scale, isDarkMode);

    // =========================================================================
    // 4. クリックで広がる同心円音響トンネル波紋＆多面体バースト
    // =========================================================================
    this.updateAndDrawRipples(scale, dt, isDarkMode);

    // =========================================================================
    // 5. 舞い散るモノクロームの紫陽花ガク（4枚花弁）とカーソル軌跡
    // =========================================================================
    this.updateAndDrawPetals(w, h, scale, dt, isDarkMode);
    this.updateAndDrawCursorTrail(scale, dt, isDarkMode);

    // =========================================================================
    // 6. 控えめな詩的フレーズ刻印とモノクローム・ビネット
    // =========================================================================
    this.drawSubtleInscriptionAndVignette(w, h, scale, isDarkMode);
  }

  /**
   * 1. 背景と斜めダイヤ格子／方眼紙・チェッカーボード（市松模様）浸食波の描画
   */
  drawBackgroundAndGrid(w, h, scale, isDarkMode, glow) {
    const p = this.p;
    if (isDarkMode) {
      p.background(10, 11, 13);
    } else {
      p.background(250, 250, 252);
    }

    p.push();
    p.translate(w * 0.5 - this.parallaxX * 0.2, h * 0.5 - this.parallaxY * 0.2);
    // MV特有の少し傾いた方眼紙／斜めダイヤグリッド
    const gridAngle = this.modeIndex === 3 ? -0.18 : p.QUARTER_PI;
    p.rotate(gridAngle);

    const step = (this.modeIndex === 3 ? 38 : 48) * scale;
    const span = Math.max(w, h) * 0.95;

    // 方眼紙の細線と交点ドット（hydrangea_maxres1.jpg / hydrangea_maxres2.jpg）
    p.stroke(isDarkMode ? [220, 224, 230, 28] : [30, 32, 36, 26]);
    p.strokeWeight(0.8 * scale);
    for (let x = -span; x <= span; x += step) {
      p.line(x, -span, x, span);
      p.line(-span, x, span, x);
    }

    // 交点の小さな十字ティック（方眼紙の質感）
    if (this.modeIndex === 3 || this.modeIndex === 1) {
      p.stroke(isDarkMode ? [230, 235, 240, 55] : [20, 22, 26, 65]);
      p.strokeWeight(1.1 * scale);
      for (let x = -span; x <= span; x += step) {
        for (let y = -span; y <= span; y += step) {
          p.line(x - 2.5 * scale, y, x + 2.5 * scale, y);
          p.line(x, y - 2.5 * scale, x, y + 2.5 * scale);
        }
      }
    }

    // 四隅を流れるチェッカーボード（市松模様）の浸食波（hydrangea_sb0.jpg Row 0-1 / hydrangea_z3_bot.png）
    p.noStroke();
    const waveOffset = this.time * 1.4;
    for (let x = -span; x < span; x += step) {
      for (let y = -span; y < span; y += step) {
        const distFromCenter = Math.sqrt(x * x + y * y) / span;
        if (distFromCenter < 0.52) continue;

        const ix = Math.round(x / step);
        const iy = Math.round(y / step);
        if ((ix + iy) % 2 === 0) {
          const wave = Math.sin(distFromCenter * 7.0 - waveOffset + (ix - iy) * 0.25);
          if (wave > 0.25) {
            const alpha = p.map(wave, 0.25, 1.0, 5, isDarkMode ? 48 : 38);
            p.fill(isDarkMode ? [235, 238, 242, alpha] : [24, 26, 30, alpha]);
            p.rect(x, y, step, step);
          }
        }
      }
    }

    p.pop();
  }

  /**
   * Mode 0: 漆黒の点描天球と同心円軌道・公転する3D多面体（hydrangea_maxres3.jpg 完全再現）
   */
  drawDarkCosmosSphereAndPolyhedra(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    p.push();
    p.translate(cx, cy);

    // 1. 外周へ広がる細い白の同心円軌道リング（hydrangea_maxres3.jpg）
    p.noFill();
    const radii = [165, 255, 355, 465];
    for (let i = 0; i < radii.length; i++) {
      const r = (radii[i] + Math.sin(this.time * 1.5 + i) * 4 + this.pulseBoost * 10) * scale;
      p.stroke(235, 238, 244, (185 - i * 32) * (0.7 + 0.3 * glow));
      p.strokeWeight((i === 0 || i === 1 ? 1.5 : 1.0) * scale);
      p.circle(0, 0, r * 2);
    }

    // 2. 点描（スティップル）グレイン層と中央の純白球体
    if (this.stippleSphereDark) {
      p.imageMode(p.CENTER);
      const dispSize = (460 + this.pulseBoost * 18) * scale;
      p.image(this.stippleSphereDark, 0, 0, dispSize, dispSize);
    }

    // 3. 同心円軌道上を公転する3Dワイヤーフレーム多面体と紫陽花小花
    for (let i = 0; i < this.polyhedra.length; i++) {
      const poly = this.polyhedra[i];
      poly.angle += poly.orbitSpeed * 0.015 * speedMult * dt;
      poly.rotX += poly.rotSpeedX * dt;
      poly.rotY += poly.rotSpeedY * dt;

      const orbitR = radii[i % radii.length] * scale;
      const px = Math.cos(poly.angle) * orbitR;
      const py = Math.sin(poly.angle) * orbitR;

      this.draw3DPolyhedron(px, py, poly.size, poly.rotX, poly.rotY, poly.shapeType, true, scale);
    }

    p.pop();
  }

  /**
   * Mode 1: 純白の点描球体と楕円リング・公転する多面体（hydrangea_maxresdefault.jpg 完全再現）
   */
  drawLightStippledSphereAndPolyhedra(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    p.push();
    p.translate(cx, cy - 18 * scale);

    // 1. 背面を通る点描の楕円リング軌道
    p.noFill();
    p.stroke(35, 38, 44, 155);
    p.strokeWeight(1.3 * scale);
    p.push();
    p.rotate(-0.08);
    p.arc(0, 0, 560 * scale, 135 * scale, p.PI, p.TWO_PI);
    p.pop();

    // 2. 中央の点描陰影球体（hydrangea_maxresdefault.jpg）
    if (this.stippleSphereLight) {
      p.imageMode(p.CENTER);
      const sphSize = (285 + this.pulseBoost * 12) * scale;
      p.image(this.stippleSphereLight, 0, 0, sphSize, sphSize);
      // 球体の繊細な輪郭線
      p.noFill();
      p.stroke(24, 26, 30, 210);
      p.strokeWeight(1.4 * scale);
      p.circle(0, 0, sphSize * 0.92);
    }

    // 3. 前面を通る楕円リング軌道と、その上を公転する立体多面体（正二十面体・正十二面体・四面体）
    p.noFill();
    p.stroke(24, 26, 30, 210);
    p.strokeWeight(1.5 * scale);
    p.push();
    p.rotate(-0.08);
    p.arc(0, 0, 560 * scale, 135 * scale, 0, p.PI);
    p.pop();

    for (let i = 0; i < this.polyhedra.length; i++) {
      const poly = this.polyhedra[i];
      poly.angle += poly.orbitSpeed * 0.015 * speedMult * dt;
      poly.rotX += poly.rotSpeedX * dt;
      poly.rotY += poly.rotSpeedY * dt;

      const px = Math.cos(poly.angle) * poly.orbitRadiusX;
      const py = Math.sin(poly.angle) * poly.orbitRadiusY;
      this.draw3DPolyhedron(px, py, poly.size, poly.rotX, poly.rotY, poly.shapeType, false, scale);
    }

    p.pop();
  }

  /**
   * Mode 2: 「空白を埋める」四弁ダイヤ格子と中央の点描コア（hydrangea_z0_bot.png / hydrangea_z2_top.png 完全再現）
   */
  drawBlankFillingDiamondGrid(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. 画面全体を覆う「4分割ダイヤタイル（紫陽花の4枚ガク）」の自律呼吸＆空白充填アニメーション
    for (let i = 0; i < this.diamondTiles.length; i++) {
      const tile = this.diamondTiles[i];
      // 周期的な波で「空白」が埋まったり消えたりする
      const autoWave = Math.sin(this.time * 1.4 - tile.phase);
      if (p.frameCount % 45 === 0 && p.random() < 0.08) {
        tile.targetFill = autoWave > 0.1 ? 1.0 : 0.0;
      }
      tile.fillLevel = p.lerp(tile.fillLevel, tile.targetFill, 0.08 * dt);

      if (tile.fillLevel < 0.03) continue;

      const tx = tile.x - this.parallaxX * 0.4;
      const ty = tile.y - this.parallaxY * 0.4;
      this.drawFourPetalDiamondTile(tx, ty, tile.size, tile.fillLevel, scale);
    }

    // 2. 中央で静かに脈動する点描球体と公転する多面体
    p.push();
    p.translate(cx, cy);
    if (this.stippleSphereDark) {
      p.imageMode(p.CENTER);
      p.image(this.stippleSphereDark, 0, 0, 290 * scale, 290 * scale);
    }
    p.noFill();
    p.stroke(240, 242, 246, 195);
    p.strokeWeight(1.4 * scale);
    p.circle(0, 0, 235 * scale);
    p.pop();
  }

  /**
   * 紫陽花の装飾花（4枚のガク）を幾何学化した「4分割ダイヤタイル（◇）」を描画
   * （hydrangea_z0_bot.png 3段目 & hydrangea_z2_top.png 1〜2段目の完全再現）
   */
  drawFourPetalDiamondTile(x, y, size, fillLevel, scale) {
    const p = this.p;
    const s = size * (0.65 + 0.35 * fillLevel);
    const half = s * 0.5;
    const gap = 2.2 * scale;

    p.push();
    p.translate(x, y);
    p.rotate(p.QUARTER_PI); // 正方形を45度傾けてダイヤ（◇）にする

    // 外枠の角丸正方形
    p.noFill();
    p.stroke(235, 238, 242, 195 * fillLevel);
    p.strokeWeight(1.4 * scale);
    p.rectMode(p.CENTER);
    p.rect(0, 0, s, s, 4 * scale);

    // 4つの象限（紫陽花の4枚のガク）に分割された内部タイル
    const qSize = half - gap * 1.5;
    const offsets = [
      [-half * 0.48, -half * 0.48],
      [half * 0.48, -half * 0.48],
      [-half * 0.48, half * 0.48],
      [half * 0.48, half * 0.48]
    ];

    for (let q = 0; q < 4; q++) {
      const ox = offsets[q][0];
      const oy = offsets[q][1];
      // 外側の白い光沢縁取りと内側のダークグレー面（MVの立体ガラスタイル感）
      p.fill(235, 238, 244, 55 * fillLevel);
      p.stroke(245, 248, 252, 220 * fillLevel);
      p.strokeWeight(1.2 * scale);
      p.rect(ox, oy, qSize, qSize, 2.5 * scale);
    }

    // 中心の花芯（小さな丸／ダイヤ）
    p.fill(250, 252, 255, 245 * fillLevel);
    p.noStroke();
    p.circle(0, 0, 5.5 * scale);

    p.pop();
  }

  /**
   * Mode 3: 傾いた方眼紙とモノクローム紫陽花の花房・縦スリット窓・黒帯（hydrangea_maxres2.jpg / hydrangea_maxres1.jpg 完全再現）
   */
  drawGraphPaperHydrangeaBouquet(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. 背後の縦スリット窓（hydrangea_maxres1.jpg の中央縦スリット表現）
    p.push();
    p.rectMode(p.CENTER);
    p.noStroke();
    p.fill(32, 34, 38, 38);
    p.rect(cx, cy, 88 * scale, h * 0.78);
    p.stroke(24, 26, 30, 140);
    p.strokeWeight(1.2 * scale);
    p.noFill();
    p.rect(cx, cy, 88 * scale, h * 0.78);
    p.pop();

    // 2. 中央に咲き誇るモノクローム紫陽花の花房（hydrangea_maxres2.jpg）
    p.push();
    p.translate(cx, cy);

    for (let i = 0; i < this.hydrangeaFlorets.length; i++) {
      const fl = this.hydrangeaFlorets[i];
      const breathe = 1.0 + Math.sin(this.time * 1.8 + fl.phase) * 0.04 + this.pulseBoost * 0.06;
      const fx = fl.ox * breathe;
      const fy = fl.oy * breathe;
      const rot = fl.rot + Math.sin(this.time * 0.9 + fl.phase) * 0.06;

      this.drawSingleHydrangeaFloret(fx, fy, fl.size, rot, fl.shade, scale);
    }

    // 3. 花房の中央を横断する乾いた黒ブラシの検閲帯と幾何学四弁ダイヤモチーフ（歌詞文字は出さず純粋な造形として表現）
    const barW = 290 * scale;
    const barH = 44 * scale;
    p.rectMode(p.CENTER);
    p.noStroke();
    p.fill(14, 15, 18, 240);
    p.rect(0, 0, barW, barH);

    // 両端のかすれたドライブラシ・スティップル粒子（hydrangea_maxres2.jpg の黒帯端のザラつき）
    for (let s = 0; s < 65; s++) {
      const side = s % 2 === 0 ? 1 : -1;
      const sx = side * (barW * 0.48 + ((s * 17) % 28) * scale);
      const sy = (((s * 29) % 42) - 21) * scale;
      p.fill(14, 15, 18, 195);
      p.circle(sx, sy, 2.8 * scale);
    }

    // 黒帯の中に並ぶ繊細な白線の四弁ダイヤ記号群
    for (let k = -2; k <= 2; k++) {
      const dx = k * 46 * scale;
      const fillAmt = 0.65 + 0.35 * Math.sin(this.time * 2.0 + k * 0.8);
      this.drawFourPetalDiamondTile(dx, 0, 20 * scale, fillAmt, scale);
    }

    p.pop();
  }

  /**
   * 紫陽花の装飾花（4枚の丸みを帯びたガク＋中心の小さな両性花）1輪をモノクローム点描調で描画
   */
  drawSingleHydrangeaFloret(x, y, size, rot, shade, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(rot);

    const petalR = size * 0.44;
    // 4枚のガク（花弁）
    for (let k = 0; k < 4; k++) {
      const a = (p.TWO_PI / 4) * k;
      p.push();
      p.rotate(a);
      const pShade = p.constrain(shade + (k % 2 === 0 ? 12 : -14), 45, 252);
      p.fill(pShade, pShade, pShade + 2, 245);
      p.stroke(28, 30, 34, 185);
      p.strokeWeight(1.1 * scale);

      // 紫陽花特有の少しひし形がかった丸いガク形状
      p.beginShape();
      p.vertex(0, 0);
      p.bezierVertex(petalR * 0.45, -petalR * 0.55, petalR * 1.05, -petalR * 0.45, petalR * 1.15, 0);
      p.bezierVertex(petalR * 1.05, petalR * 0.45, petalR * 0.45, petalR * 0.55, 0, 0);
      p.endShape(p.CLOSE);

      // ガクの中央脈
      p.stroke(40, 42, 48, 85);
      p.strokeWeight(0.7 * scale);
      p.line(2 * scale, 0, petalR * 0.85, 0);
      p.pop();
    }

    // 中心の小さな花芯
    p.fill(32, 34, 38, 240);
    p.noStroke();
    p.circle(0, 0, size * 0.16);
    p.fill(245, 248, 250, 230);
    p.circle(0, 0, size * 0.07);

    p.pop();
  }

  /**
   * 3D多面体（正二十面体・正八面体・四面体）の点描シェーディング＋ワイヤーフレーム描画
   * （hydrangea_maxresdefault.jpg / hydrangea_maxres3.jpg の公転する多面体）
   */
  draw3DPolyhedron(x, y, size, rotX, rotY, shapeType, isDarkBg, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(rotX * 0.5);

    const sides = shapeType === 0 ? 6 : shapeType === 1 ? 4 : 3;
    const outerVerts = [];
    for (let i = 0; i < sides; i++) {
      const a = (p.TWO_PI / sides) * i + rotY;
      outerVerts.push({
        x: Math.cos(a) * size,
        y: Math.sin(a) * size
      });
    }

    // 内側の頂点（立体的なファセット面を作る）
    const innerX = Math.cos(rotX) * size * 0.28;
    const innerY = Math.sin(rotY) * size * 0.28;

    // ファセット面の明暗塗り分け
    for (let i = 0; i < sides; i++) {
      const v1 = outerVerts[i];
      const v2 = outerVerts[(i + 1) % sides];
      const lum = isDarkBg
        ? 35 + ((i * 45) % 140)
        : 120 + ((i * 42) % 130);

      p.fill(lum, lum, lum + 2, 225);
      p.stroke(isDarkBg ? [242, 245, 250, 235] : [20, 22, 26, 225]);
      p.strokeWeight(1.3 * scale);
      p.triangle(innerX, innerY, v1.x, v1.y, v2.x, v2.y);
    }

    p.pop();
  }

  /**
   * 3. 浮遊する3Dワイヤーフレーム直方体（MVサビの透明な立体箱）の描画
   */
  drawFloatingWireframeBoxes(w, h, scale, isDarkMode) {
    const p = this.p;
    p.push();
    p.noFill();
    p.stroke(isDarkMode ? [225, 230, 238, 95] : [28, 30, 36, 110]);
    p.strokeWeight(1.1 * scale);

    for (let i = 0; i < this.wireframeBoxes.length; i++) {
      const b = this.wireframeBoxes[i];
      const bx = b.xRatio * w - this.parallaxX * b.depth;
      const by = b.yRatio * h + Math.sin(this.time * 1.2 + b.driftPhase) * 10 * scale - this.parallaxY * b.depth;

      p.push();
      p.translate(bx, by);
      p.rotate(b.tilt + Math.sin(this.time * 0.7 + b.driftPhase) * 0.04);

      const hw = b.w * 0.5;
      const hh = b.h * 0.5;
      const ox = b.d * 0.45;
      const oy = -b.d * 0.35;

      // 前面の長方形
      p.rect(-hw, -hh, b.w, b.h);
      // 背面の長方形
      p.rect(-hw + ox, -hh + oy, b.w, b.h);
      // 4つの奥行き稜線を結ぶ
      p.line(-hw, -hh, -hw + ox, -hh + oy);
      p.line(hw, -hh, hw + ox, -hh + oy);
      p.line(-hw, hh, -hw + ox, hh + oy);
      p.line(hw, hh, hw + ox, hh + oy);

      p.pop();
    }
    p.pop();
  }

  /**
   * 4. クリックで広がる同心円音響トンネル波紋＆多面体の更新・描画
   */
  updateAndDrawRipples(scale, dt, isDarkMode) {
    const p = this.p;
    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const rp = this.ripples[i];
      rp.radius = p.lerp(rp.radius, rp.maxRadius, 0.07 * dt);
      rp.rot += 0.03 * dt;
      rp.alpha -= 3.2 * dt;

      if (rp.alpha <= 0) {
        this.ripples.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(rp.x, rp.y);
      p.noFill();
      const col = isDarkMode ? [242, 245, 250] : [20, 22, 26];

      // 3重の同心円音響トンネルリング
      for (let r = 0; r < 3; r++) {
        const rad = rp.radius * (1.0 - r * 0.18);
        if (rad > 2) {
          p.stroke(col[0], col[1], col[2], rp.alpha * (1.0 - r * 0.28));
          p.strokeWeight((1.8 - r * 0.4) * scale);
          p.circle(0, 0, rad * 2);
        }
      }

      // 中心で回転する多面体
      this.draw3DPolyhedron(0, 0, rp.polySize * (rp.alpha / 255), rp.rot, -rp.rot, 0, isDarkMode, scale);
      p.pop();
    }
  }

  /**
   * 5. 舞い散るモノクロームの花弁・点描粒子の更新・描画
   */
  updateAndDrawPetals(w, h, scale, dt, isDarkMode) {
    const p = this.p;
    p.push();
    for (let i = this.petals.length - 1; i >= 0; i--) {
      const pt = this.petals[i];
      pt.x += (pt.vx + Math.sin(this.time * 1.5 + pt.phase) * 0.35) * dt;
      pt.y += pt.vy * dt;
      pt.rot += pt.rotSpeed * dt;

      if (pt.temporary) {
        pt.vx *= 0.96;
        pt.vy *= 0.96;
        pt.alpha -= 4.2 * dt;
        if (pt.alpha <= 0) {
          this.petals.splice(i, 1);
          continue;
        }
      } else {
        if (pt.y < -15) pt.y = h + 15;
        if (pt.x < -15) pt.x = w + 15;
        if (pt.x > w + 15) pt.x = -15;
      }

      const alpha = pt.temporary ? pt.alpha : 185;
      p.push();
      p.translate(pt.x, pt.y);
      p.rotate(pt.rot);
      if (pt.isPetal) {
        p.fill(pt.gray, pt.gray, pt.gray + 2, alpha);
        p.stroke(isDarkMode ? 240 : 24, alpha * 0.7);
        p.strokeWeight(0.8 * scale);
        p.ellipse(0, 0, pt.size * 1.4, pt.size * 0.85);
      } else {
        p.noStroke();
        p.fill(isDarkMode ? 235 : 35, alpha);
        p.circle(0, 0, pt.size * 0.45);
      }
      p.pop();
    }
    p.pop();
  }

  /**
   * カーソル移動軌跡の小さな4分割ダイヤの描画
   */
  updateAndDrawCursorTrail(scale, dt, isDarkMode) {
    const p = this.p;
    if (this.cursorTrail.length === 0) return;

    p.push();
    p.rectMode(p.CENTER);
    p.noFill();
    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 7.0 * dt;
      tr.rot += 0.05 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(tr.x, tr.y);
      p.rotate(p.QUARTER_PI + tr.rot);
      p.stroke(isDarkMode ? [240, 244, 248, tr.alpha] : [22, 24, 28, tr.alpha]);
      p.strokeWeight(1.1);
      p.rect(0, 0, tr.size, tr.size, 2);
      p.line(-tr.size * 0.5, 0, tr.size * 0.5, 0);
      p.line(0, -tr.size * 0.5, 0, tr.size * 0.5);
      p.pop();
    }
    p.pop();
  }

  /**
   * 6. 四隅のモノクローム・ビネット
   */
  drawSubtleInscriptionAndVignette(w, h, scale, isDarkMode) {
    const p = this.p;
    p.push();

    // 四隅の淡いモノクロームビネット
    p.noFill();
    const layers = 8;
    for (let i = 1; i <= layers; i++) {
      const t = i / layers;
      p.stroke(isDarkMode ? 4 : 185, t * 20);
      p.strokeWeight((layers - i + 1) * 5);
      p.rect(0, 0, w, h);
    }

    p.pop();
  }
}
